import { useState } from 'react'
import { useLocation } from '../../../features/locations/use-location'
import {
  canEditWanSettings,
  useSaveWanSettings,
  useWanSettings,
  type WanDataRecord,
  type WanSettingsData,
  type WanSettingsUpdate,
} from '../../../features/locations/use-wan-settings'
import { ApiError } from '../../../lib/api-client'
import { useIdOrCode } from '../../use-id-or-code'
import { Badge, Card, Crumb, mono, Notice, PageHeader, SkeletonCards } from './ui'

const input =
  'h-9 w-full rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-[14px] text-[var(--color-fg)] outline-none transition-shadow placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:shadow-[0_0_0_3px_var(--v1-accent-soft)] disabled:cursor-not-allowed disabled:opacity-50'
const th = 'h-10 px-5 text-left text-[12px] font-medium uppercase tracking-[0.04em] text-[var(--color-muted)]'
const td = 'h-11 border-t border-[var(--color-border)] px-5'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the form control is passed as children and sits inside this label
    <label className="block">
      <span className="mb-1 block text-[12px] font-medium text-[var(--color-muted)]">{label}</span>
      {children}
    </label>
  )
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return "You don't have permission to change WAN settings for this store."
    if (e.status === 400)
      return 'These settings were rejected. Check the values, and that the store is fully provisioned.'
    return `The change couldn't be saved (error ${e.status}). Please try again.`
  }
  return "The change couldn't be saved. Please try again."
}

function SaveBar({
  canEdit,
  dirty,
  pending,
  ok,
  error,
  onReset,
}: {
  canEdit: boolean
  dirty: boolean
  pending: boolean
  ok: boolean
  error: unknown
  onReset: () => void
}) {
  return (
    <div className="flex items-center gap-3 border-t border-[var(--color-border)] px-5 py-3.5">
      <button
        type="submit"
        disabled={!canEdit || !dirty || pending}
        className="h-9 rounded-[10px] bg-[var(--color-accent)] px-4 text-[14px] font-medium text-[var(--color-accent-fg)] transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-40"
      >
        {pending ? 'Saving...' : 'Save changes'}
      </button>
      <button
        type="button"
        onClick={onReset}
        disabled={!dirty || pending}
        className="h-9 rounded-[10px] px-3 text-[14px] font-medium text-[var(--color-accent)] hover:bg-[var(--v1-accent-soft)] disabled:cursor-not-allowed disabled:opacity-40"
      >
        Reset
      </button>
      <div aria-live="polite" className="text-[13px]">
        {error ? <span style={{ color: 'var(--v1-bad)' }}>{errorMessage(error)}</span> : null}
        {ok && !error && !dirty ? <span style={{ color: 'var(--v1-ok)' }}>Saved.</span> : null}
      </div>
    </div>
  )
}

interface MerakiRow {
  enabled: boolean
  mode: 'static' | 'dynamic' | ''
  address: string
  gateway: string
  carrier: string
}

const MERAKI_CARRIER_TYPE: Record<string, WanDataRecord['type']> = { wan1: 'PRIMARY', wan2: 'SECONDARY' }

function MerakiForm({
  locationId,
  data,
  interfaces,
  canEdit,
}: {
  locationId: string
  data: WanSettingsData
  interfaces: Record<
    string,
    { enabled?: boolean; assignmentMode?: 'static' | 'dynamic'; address?: string; gateway?: string }
  >
  canEdit: boolean
}) {
  const save = useSaveWanSettings()
  const initial: Record<string, MerakiRow> = Object.fromEntries(
    Object.entries(interfaces).map(([name, i]) => [
      name,
      {
        enabled: i.enabled ?? false,
        mode: i.assignmentMode ?? '',
        address: i.address ?? '',
        gateway: i.gateway ?? '',
        carrier: data.dbRecords.find((r) => r.type === MERAKI_CARRIER_TYPE[name])?.carrier ?? '',
      },
    ]),
  )
  const [rows, setRows] = useState(initial)
  const patch = (name: string, p: Partial<MerakiRow>) => {
    save.reset()
    setRows((r) => ({ ...r, [name]: { ...r[name], ...p } as MerakiRow }))
  }
  const changed = Object.keys(rows).filter((n) => JSON.stringify(rows[n]) !== JSON.stringify(initial[n]))

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const body: WanSettingsUpdate = { vendor: 'meraki', interfaces: {} }
    for (const name of changed) {
      const r = rows[name] as MerakiRow
      body.interfaces[name] = {
        enabled: r.enabled,
        ...(r.carrier.trim() ? { carrier: r.carrier.trim() } : {}),
        ...(r.mode === 'static'
          ? {
              svis: {
                ipv4: { assignmentMode: 'static' as const, address: r.address.trim(), gateway: r.gateway.trim() },
              },
            }
          : r.mode === 'dynamic'
            ? { svis: { ipv4: { assignmentMode: 'dynamic' as const } } }
            : {}),
      }
    }
    save.mutate({ locationId, body })
  }

  const names = Object.keys(rows)
  return (
    <form onSubmit={submit}>
      {names.length === 0 ? (
        <p className="px-5 py-8 text-center text-[13px] text-[var(--color-muted)]">No interfaces reported by Meraki.</p>
      ) : (
        names.map((name) => {
          const r = rows[name] as MerakiRow
          const staticMissing = r.mode === 'static' && (!r.address.trim() || !r.gateway.trim())
          return (
            <fieldset
              key={name}
              disabled={!canEdit || save.isPending}
              className="border-t border-[var(--color-border)] px-5 py-4 first:border-t-0"
            >
              <legend className="sr-only">{name}</legend>
              <div className="flex items-center justify-between">
                <span className="text-[14px] font-semibold uppercase" style={mono}>
                  {name}
                </span>
                <label className="flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={r.enabled}
                    onChange={(e) => patch(name, { enabled: e.target.checked })}
                    className="size-4 accent-[var(--color-accent)]"
                  />
                  Enabled
                </label>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-4">
                <Field label="Addressing">
                  <select
                    className={input}
                    value={r.mode}
                    onChange={(e) => patch(name, { mode: e.target.value as MerakiRow['mode'] })}
                  >
                    <option value="">Unchanged</option>
                    <option value="dynamic">DHCP</option>
                    <option value="static">Static</option>
                  </select>
                </Field>
                <Field label="Address">
                  <input
                    className={input}
                    style={mono}
                    value={r.address}
                    disabled={r.mode !== 'static'}
                    onChange={(e) => patch(name, { address: e.target.value })}
                    placeholder="203.0.113.10/30"
                  />
                </Field>
                <Field label="Gateway">
                  <input
                    className={input}
                    style={mono}
                    value={r.gateway}
                    disabled={r.mode !== 'static'}
                    onChange={(e) => patch(name, { gateway: e.target.value })}
                    placeholder="203.0.113.9"
                  />
                </Field>
                <Field label="Carrier">
                  <input
                    className={input}
                    value={r.carrier}
                    onChange={(e) => patch(name, { carrier: e.target.value })}
                    placeholder="e.g. Comcast"
                  />
                </Field>
              </div>
              {staticMissing ? (
                <p className="mt-2 text-[12px]" style={{ color: 'var(--v1-warn)' }}>
                  Static addressing needs an address and a gateway.
                </p>
              ) : null}
            </fieldset>
          )
        })
      )}
      <SaveBar
        canEdit={
          canEdit &&
          !names.some((n) => rows[n]?.mode === 'static' && (!rows[n].address.trim() || !rows[n].gateway.trim()))
        }
        dirty={changed.length > 0}
        pending={save.isPending}
        ok={save.isSuccess}
        error={save.error}
        onReset={() => {
          save.reset()
          setRows(initial)
        }}
      />
    </form>
  )
}

interface CfWan {
  id?: string
  name?: string
  static_addressing?: { address: string; gateway_address: string; secondary_address?: string }
}

function CloudflareForm({
  locationId,
  data,
  wans,
  canEdit,
}: {
  locationId: string
  data: WanSettingsData
  wans: CfWan[]
  canEdit: boolean
}) {
  const editable = wans.filter((w): w is CfWan & { id: string } => typeof w.id === 'string')
  const [wanId, setWanId] = useState(editable[0]?.id ?? '')
  const wan = editable.find((w) => w.id === wanId)
  const initial = {
    address: wan?.static_addressing?.address ?? '',
    gateway: wan?.static_addressing?.gateway_address ?? '',
    secondary: wan?.static_addressing?.secondary_address ?? '',
    carrier: data.dbRecords[0]?.carrier ?? '',
  }
  return (
    <CloudflareFields
      key={wanId}
      locationId={locationId}
      editable={editable}
      wanId={wanId}
      onWan={setWanId}
      initial={initial}
      canEdit={canEdit}
    />
  )
}

function CloudflareFields({
  locationId,
  editable,
  wanId,
  onWan,
  initial,
  canEdit,
}: {
  locationId: string
  editable: (CfWan & { id: string })[]
  wanId: string
  onWan: (id: string) => void
  initial: { address: string; gateway: string; secondary: string; carrier: string }
  canEdit: boolean
}) {
  const save = useSaveWanSettings()
  const [v, setV] = useState(initial)
  const set = (p: Partial<typeof v>) => {
    save.reset()
    setV((x) => ({ ...x, ...p }))
  }
  const dirty = JSON.stringify(v) !== JSON.stringify(initial)
  const valid = v.address.trim() !== '' && v.gateway.trim() !== ''

  if (editable.length === 0)
    return (
      <p className="px-5 py-8 text-center text-[13px] text-[var(--color-muted)]">
        No editable Cloudflare WANs were found.
      </p>
    )

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate({
          locationId,
          body: {
            vendor: 'cloudflare',
            wanId,
            ...(v.carrier.trim() ? { carrier: v.carrier.trim() } : {}),
            staticAddressing: {
              address: v.address.trim(),
              gateway_address: v.gateway.trim(),
              ...(v.secondary.trim() ? { secondary_address: v.secondary.trim() } : {}),
            },
          },
        })
      }}
    >
      <fieldset disabled={!canEdit || save.isPending} className="px-5 py-4">
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="WAN">
            <select className={input} value={wanId} onChange={(e) => onWan(e.target.value)}>
              {editable.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name ?? w.id}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Carrier">
            <input
              className={input}
              value={v.carrier}
              onChange={(e) => set({ carrier: e.target.value })}
              placeholder="e.g. Comcast"
            />
          </Field>
          <Field label="Address">
            <input
              className={input}
              style={mono}
              value={v.address}
              onChange={(e) => set({ address: e.target.value })}
              placeholder="203.0.113.10/30"
            />
          </Field>
          <Field label="Gateway">
            <input
              className={input}
              style={mono}
              value={v.gateway}
              onChange={(e) => set({ gateway: e.target.value })}
              placeholder="203.0.113.9"
            />
          </Field>
          <Field label="Secondary address (optional)">
            <input
              className={input}
              style={mono}
              value={v.secondary}
              onChange={(e) => set({ secondary: e.target.value })}
            />
          </Field>
        </div>
        {!valid ? (
          <p className="mt-2 text-[12px]" style={{ color: 'var(--v1-warn)' }}>
            Address and gateway are required.
          </p>
        ) : null}
      </fieldset>
      <SaveBar
        canEdit={canEdit && valid}
        dirty={dirty}
        pending={save.isPending}
        ok={save.isSuccess}
        error={save.error}
        onReset={() => {
          save.reset()
          setV(initial)
        }}
      />
    </form>
  )
}

export function PorcelainWanSettings() {
  const idOrCode = useIdOrCode()
  const loc = useLocation(idOrCode)
  const { data, isLoading, error } = useWanSettings(idOrCode)
  const locationId = loc.data?.id

  const header = (
    <PageHeader
      eyebrow={<Crumb idOrCode={idOrCode} trail="wan" />}
      title="WAN settings"
      subtitle="Internet circuits and addressing for this store."
      right={data?.lifecycle ? <Badge label={data.lifecycle} /> : undefined}
    />
  )

  if (isLoading || loc.isLoading)
    return (
      <section>
        {header}
        <SkeletonCards rows={2} />
      </section>
    )
  if (error || loc.error || !data || !locationId)
    return (
      <section>
        {header}
        <Notice
          tone="error"
          title="We couldn't load WAN settings"
          body={error ? 'Check your connection and try again in a moment.' : 'This location could not be found.'}
        />
      </section>
    )

  const canEdit = canEditWanSettings(data)
  const vs = data.vendorState

  return (
    <section>
      {header}

      {canEdit ? null : (
        <output
          className="mt-6 block rounded-[12px] px-5 py-3 text-[13px]"
          style={{ background: 'var(--v1-warn-bg)', color: 'var(--v1-warn)' }}
        >
          Editing is available once this store is provisioned
          {data.lifecycle ? ` (currently ${data.lifecycle.toLowerCase()})` : ''}. Settings below are read-only.
        </output>
      )}

      <div className="mt-6 grid gap-5">
        <Card
          title="Circuits"
          aside={
            data.routerSerial ? (
              <span className="text-[12px] text-[var(--color-muted)]" style={mono}>
                {data.routerSerial}
              </span>
            ) : undefined
          }
        >
          {data.dbRecords.length === 0 ? (
            <p className="px-5 py-8 text-center text-[13px] text-[var(--color-muted)]">
              No circuits recorded for this store.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[14px]">
                <thead>
                  <tr>
                    <th className={th}>Circuit</th>
                    <th className={th}>Carrier</th>
                    <th className={th}>Addressing</th>
                    <th className={th}>IP</th>
                    <th className={th}>Gateway</th>
                    <th className={th}>Down / Up</th>
                    <th className={th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.dbRecords.map((r) => (
                    <tr key={r.id} className="transition-colors hover:bg-[var(--v1-accent-soft)]">
                      <td className={`${td} font-medium`}>{r.type.charAt(0) + r.type.slice(1).toLowerCase()}</td>
                      <td className={td}>{r.carrier || '-'}</td>
                      <td className={td}>{r.assignmentType === 'STATIC' ? 'Static' : 'DHCP'}</td>
                      <td className={td} style={mono}>
                        {r.assignedCidr ?? r.assignedIp ?? '-'}
                      </td>
                      <td className={td} style={mono}>
                        {r.gw ?? '-'}
                      </td>
                      <td className={td} style={mono}>
                        {r.rxSpeed ?? '-'} / {r.txSpeed ?? '-'}
                      </td>
                      <td className={td}>
                        <Badge label={r.status ? 'Up' : 'Down'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Edit settings">
          {vs.vendor === 'meraki' ? (
            <MerakiForm
              key={JSON.stringify(vs.interfaces)}
              locationId={locationId}
              data={data}
              interfaces={vs.interfaces ?? {}}
              canEdit={canEdit}
            />
          ) : vs.vendor === 'cloudflare' ? (
            <CloudflareForm
              key={JSON.stringify(vs.wans)}
              locationId={locationId}
              data={data}
              wans={vs.wans ?? []}
              canEdit={canEdit}
            />
          ) : (
            <p className="px-5 py-10 text-center text-[13px] text-[var(--color-muted)]">
              This store's edge device ({data.edgeVendor ?? 'unknown vendor'}) doesn't support editing WAN settings
              here.
            </p>
          )}
        </Card>
      </div>
    </section>
  )
}
